import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { SUBJECTS_FALLBACK } from "../lib/subjects";
import { toast } from "sonner";
import { AlertTriangle, Check, Plus, Trash2, Sparkles, Crown, Eye, Lock, Globe, Share2, Copy, X, Clock, MapPin, BarChart3 } from "lucide-react";
import { openRazorpayCheckout } from "../lib/razorpay";
import { useAuth } from "../context/AuthContext";
import TeacherCoursesPanel from "../components/TeacherCoursesPanel";
import NotificationBanner from "../components/NotificationBanner";
import { CancelButton, PunchInButton, DemoCancelButton, StartSessionButton, ReportSessionButton } from "../components/SessionActions";
import { WalletPanel } from "../components/WalletPanel";
import { MentorSubscriptionPanel } from "../components/MentorSubscriptionPanel";

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [formInitialized, setFormInitialized] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [subjects, setSubjects] = useState(SUBJECTS_FALLBACK);
  const [savingProfile, setSavingProfile] = useState(false);
  const [featuredPlans, setFeaturedPlans] = useState({});
  const [featuredStatus, setFeaturedStatus] = useState({ featured: false, featured_until: null });
  const [activatingPlan, setActivatingPlan] = useState(null);
  const [demoBookings, setDemoBookings] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareExpiryDays, setShareExpiryDays] = useState(7);
  const [creatingShare, setCreatingShare] = useState(false);

  // Profile form
  const [form, setForm] = useState({
    bio: "",
    qualifications: "",
    experience_years: 0,
    fees_per_hour: 0,
    location: "",
    subjects: [],
    teaching_locations: [],
    phone: "",
  });

  // Slot form
  const [slotForm, setSlotForm] = useState({
    date: "",
    start_time: "10:00",
    end_time: "11:00",
    max_bookings: 1,
  });

  const load = async () => {
    try {
      const [p, b, s, subs, fp, fs, demos, sl] = await Promise.all([
        api.get("/teacher/me"),
        api.get("/bookings/me"),
        api.get("/teacher/availability"),
        api.get("/subjects"),
        api.get("/featured/plans"),
        api.get("/teacher/featured/status"),
        api.get("/demo/me"),
        api.get("/teacher/documents/share-links").catch(() => ({ data: [] })),
      ]);
      setProfile(p.data);
      setBookings(b.data);
      setSlots(s.data);
      setSubjects(subs.data || SUBJECTS_FALLBACK);
      setFeaturedPlans(fp.data || {});
      setFeaturedStatus(fs.data || { featured: false, featured_until: null });
      setDemoBookings(demos.data || []);
      setShareLinks(sl.data || []);
      if (p.data && !formInitialized) {
        setForm({
          bio: p.data.bio || "",
          qualifications: p.data.qualifications || "",
          experience_years: p.data.experience_years || 0,
          fees_per_hour: p.data.fees_per_hour || 0,
          location: p.data.location || "",
          subjects: p.data.subjects || [],
          teaching_locations: p.data.teaching_locations || [],
          phone: p.data.phone || "",
        });
        setFormInitialized(true);
      }
    } catch (e) {
      toast.error("Failed to load");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.put("/teacher/profile", {
        ...form,
        experience_years: Number(form.experience_years),
        fees_per_hour: Number(form.fees_per_hour),
      });
      setProfile(data);
      // If the slot form is fully filled, also persist that slot in the same click
      const hasPendingSlot =
        slotForm.date && slotForm.start_time && slotForm.end_time && Number(slotForm.max_bookings) >= 1;
      if (hasPendingSlot) {
        try {
          await api.post("/teacher/availability", {
            ...slotForm,
            max_bookings: Number(slotForm.max_bookings),
          });
          setSlotForm({ date: "", start_time: "10:00", end_time: "11:00", max_bookings: 1 });
          await load();
          toast.success("Profile and slot saved");
        } catch (err) {
          toast.success("Profile saved");
          toast.error(formatApiError(err.response?.data?.detail) || "Slot could not be added");
        }
      } else {
        toast.success("Profile saved");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadDoc = async (docType, file) => {
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    try {
      // Do NOT set Content-Type manually — axios will auto-add the multipart boundary.
      await api.post("/teacher/documents", fd);
      toast.success(`${docType} uploaded`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = formatApiError(detail) || e.message || "Upload failed";
      toast.error(`Upload failed: ${msg}`);
    }
  };

  const deleteDoc = async (docType) => {
    if (!window.confirm(`Delete your ${docType}? This cannot be undone.`)) return;
    try {
      await api.delete(`/teacher/documents/${docType}`);
      toast.success(`${docType} deleted`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Delete failed");
    }
  };

  const toggleDocVisibility = async (docType) => {
    const current = profile?.doc_visibility?.[docType] || (docType === "degree" ? "public" : "private");
    const next = current === "public" ? "private" : "public";
    try {
      await api.patch("/teacher/documents/visibility", { [docType]: next });
      toast.success(`${docType} is now ${next}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Update failed");
    }
  };

  const createShareLink = async () => {
    setCreatingShare(true);
    try {
      const { data } = await api.post("/teacher/documents/degree/share-link", { expires_days: shareExpiryDays });
      try {
        await navigator.clipboard.writeText(data.share_url);
        toast.success("Share link created & copied to clipboard");
      } catch {
        toast.success("Share link created");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Could not create link");
    } finally {
      setCreatingShare(false);
    }
  };

  const copyShareLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const revokeShareLink = async (token) => {
    if (!window.confirm("Revoke this share link? Anyone using it will lose access immediately.")) return;
    try {
      await api.delete(`/teacher/documents/share-links/${token}`);
      toast.success("Link revoked");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Revoke failed");
    }
  };

  const fmtExpiry = (iso) => {
    try {
      const ms = new Date(iso).getTime() - Date.now();
      if (ms <= 0) return "expired";
      const days = Math.floor(ms / (1000 * 60 * 60 * 24));
      const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      if (days >= 1) return `${days}d ${hours}h left`;
      const mins = Math.max(1, Math.floor(ms / (1000 * 60)));
      return `${hours}h ${mins % 60}m left`;
    } catch {
      return "";
    }
  };

  // Open a document inline. Photo is publicly readable; aadhar + private docs need auth — fetch as blob.
  const openDoc = async (docType, path) => {
    if (!path) return;
    const isPublic =
      docType === "photo" ||
      (profile?.doc_visibility?.[docType] || (docType === "aadhar" ? "private" : "public")) === "public";
    try {
      if (!isPublic) {
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        window.open(`${API}/files/${path}`, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open document");
    }
  };

  const addSlot = async (e) => {
    e.preventDefault();
    try {
      await api.post("/teacher/availability", {
        ...slotForm,
        max_bookings: Number(slotForm.max_bookings),
      });
      toast.success("Slot added");
      setSlotForm({ date: "", start_time: "10:00", end_time: "11:00", max_bookings: 1 });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const removeSlot = async (id) => {
    try {
      await api.delete(`/teacher/availability/${id}`);
      toast.success("Slot removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
    }
  };

  const respondBooking = async (bookingId, action, note = "") => {
    try {
      await api.post(`/teacher/bookings/${bookingId}/respond`, { action, note });
      toast.success(action === "approve" ? "Booking approved — explorer notified" : "Booking declined — explorer notified");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const activateFeatured = async (planKey) => {
    setActivatingPlan(planKey);
    try {
      const { data } = await api.post("/teacher/featured/create-order", { plan: planKey });
      openRazorpayCheckout({
        orderId: data.order_id,
        amount: data.amount,
        currency: data.currency,
        keyId: data.key_id,
        name: "Rate-ED",
        description: `Featured Listing · ${data.plan_label}`,
        prefill: { name: user?.name || "", email: user?.email || "" },
        onSuccess: async (response) => {
          try {
            const verify = await api.post("/teacher/featured/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful — your profile is now Featured!");
            setFeaturedStatus({ featured: true, featured_until: verify.data.featured_until, plan: verify.data.plan });
            await load();
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Payment verification failed");
          } finally {
            setActivatingPlan(null);
          }
        },
        onDismiss: () => {
          toast("Payment cancelled", { description: "You can complete it anytime." });
          setActivatingPlan(null);
        },
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setActivatingPlan(null);
    }
  };

  const toggleSubject = (s) => {
    const has = form.subjects.includes(s);
    setForm((f) => ({ ...f, subjects: has ? f.subjects.filter((x) => x !== s) : [...f.subjects, s] }));
  };

  const docs = profile ? [
    { key: "photo", label: "Profile Photo", path: profile.photo_path },
    { key: "aadhar", label: "Aadhar (ID Proof)", path: null },
    { key: "degree", label: "Degree Certificate", path: null },
  ] : [];

  // Recompute doc status from raw profile flags
  const missingDocs = profile && !profile.documents_complete;

  return (
    <div className="bg-peach min-h-screen" data-testid="teacher-dashboard-page">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10">
        <NotificationBanner />
        <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase" style={{ color: "#13A699" }}>
          Mentor Dashboard
        </h1>
        {user && (
          <Link
            to={`/teacher/${user.id}/performance`}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-2xl font-bold text-sm border-2 hover:bg-[#13A699] hover:text-white transition"
            style={{ borderColor: "#13A699", color: "#13A699" }}
            data-testid="my-performance-link"
          >
            View My Performance &amp; Badges →
          </Link>
        )}

        {/* ALERTS */}
        {profile && (!profile.profile_complete || missingDocs) && (
          <div
            className="mt-5 rounded-2xl p-4 flex items-start gap-3"
            style={{ background: "#FFD70833", border: "2px dashed #FFD708" }}
            data-testid="profile-alerts"
          >
            <AlertTriangle className="h-5 w-5 mt-0.5" style={{ color: "#13A699" }} />
            <div>
              <div className="font-bold" style={{ color: "#13A699" }}>Action needed</div>
              <ul className="text-sm mt-1 list-disc pl-5" style={{ color: "#13A699" }}>
                {!profile.profile_complete && <li data-testid="alert-profile-incomplete">Complete your profile (bio, qualifications, location).</li>}
                {missingDocs && <li data-testid="alert-docs-missing">Upload all required documents (Photo, Aadhar, Degree).</li>}
              </ul>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3" data-testid="teacher-quick-links">
          <Link
            to="/mentor/analytics"
            className="text-sm font-bold uppercase px-5 py-2.5 rounded-full inline-flex items-center gap-2 bg-white border-2 border-[#FFD708] text-[#13A699] hover:bg-[#FFF7ED]"
            data-testid="teacher-analytics-link"
          >
            <BarChart3 className="h-4 w-4" style={{ color: "#FFD708" }} />
            Analytics Dashboard
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#FFD708] text-[#13A699]">Pro</span>
          </Link>
        </div>

        <div className="mt-6" data-testid="teacher-subscription-wrapper">
          <MentorSubscriptionPanel />
        </div>

        <div className="mt-6" data-testid="teacher-wallet-wrapper">
          <WalletPanel role="teacher" />
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          {/* Profile */}
          <form
            onSubmit={saveProfile}
            className="lg:col-span-2 bg-white rounded-3xl p-6 border border-black/5"
            data-testid="profile-form"
          >
            <h2 className="font-display text-xl font-bold uppercase" style={{ color: "#13A699" }} data-testid="edit-profile-heading">Edit Profile</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Input label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="profile-location" />
              <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="profile-phone" />
              <Input label="Experience (years)" type="number" value={form.experience_years} onChange={(v) => setForm({ ...form, experience_years: v })} testId="profile-exp" />
              <Input label="Fees per hour (₹)" type="number" value={form.fees_per_hour} onChange={(v) => setForm({ ...form, fees_per_hour: v })} testId="profile-fees" />
            </div>
            <Textarea label="Bio" value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} testId="profile-bio" />
            <Textarea label="Qualifications" value={form.qualifications} onChange={(v) => setForm({ ...form, qualifications: v })} testId="profile-quals" />

            <h3 className="font-display font-bold mt-5 uppercase tracking-wider" style={{ color: "#13A699" }}>Mentoring Location</h3>
            <p className="text-xs" style={{ color: "#13A699" }}>Choose one or both where you can teach.</p>
            <div className="mt-2" data-testid="profile-teaching-locations">
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  setForm((f) => ({
                    ...f,
                    teaching_locations: f.teaching_locations.includes(v)
                      ? f.teaching_locations
                      : [...f.teaching_locations, v],
                  }));
                }}
                className="rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                data-testid="profile-teaching-location-select"
              >
                <option value="">+ Add a mentoring location…</option>
                {!form.teaching_locations.includes("explorer_home") && (
                  <option value="explorer_home">Explorer home visit</option>
                )}
                {!form.teaching_locations.includes("mentor_location") && (
                  <option value="mentor_location">Mentor location visit</option>
                )}
              </select>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.teaching_locations.map((loc) => (
                  <span
                    key={loc}
                    className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full"
                    style={{ background: "#FFD708", color: "#13A699" }}
                    data-testid={`teaching-location-chip-${loc}`}
                  >
                    {loc === "explorer_home" ? "Explorer home visit" : "Mentor location visit"}
                    <button
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          teaching_locations: f.teaching_locations.filter((x) => x !== loc),
                        }))
                      }
                      className="hover:text-red-600"
                      aria-label={`Remove ${loc}`}
                      data-testid={`teaching-location-remove-${loc}`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
                {form.teaching_locations.length === 0 && (
                  <span className="text-xs italic" style={{ color: "#13A69999" }}>
                    No teaching location selected — add at least one.
                  </span>
                )}
              </div>
            </div>

            <h3 className="font-display font-bold mt-5 uppercase tracking-wider" style={{ color: "#13A699" }}>Subjects you teach</h3>
            <p className="text-xs" style={{ color: "#13A699" }}>Tap any to toggle.</p>
            <div className="mt-2 max-h-72 overflow-auto rounded-xl border border-black/5 p-3" data-testid="profile-subjects-picker">
              {Object.entries(subjects).map(([cat, subs]) => (
                <div key={cat} className="mb-3">
                  <div className="text-[10px] uppercase font-bold tracking-widest" style={{ color: "#13A699" }}>{cat}</div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {Object.values(subs).flat().map((s) => {
                      const active = form.subjects.includes(s);
                      return (
                        <button
                          type="button"
                          key={s}
                          onClick={() => toggleSubject(s)}
                          className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-all duration-200`}
                          style={{
                            background: active ? "#FFD708" : "#FFF7ED",
                            color: "#13A699",
                            borderColor: active ? "#FFD708" : "#13A69922",
                          }}
                          data-testid={`subject-pill-${s}`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <button
              type="submit"
              disabled={savingProfile}
              className="btn-yellow mt-6 px-6 py-3 disabled:opacity-60"
              data-testid="profile-save-btn"
            >
              {savingProfile ? "Saving..." : "Save All"}
            </button>
            <p className="text-[11px] mt-2" style={{ color: "#13A69999" }}>
              Saves your profile and any filled-in time slot below.
            </p>
          </form>

          {/* Documents + Slots */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl p-6 border border-black/5" data-testid="documents-card">
              <h2 className="font-display text-xl font-bold uppercase" style={{ color: "#13A699" }}>Documents</h2>
              <p className="text-xs mt-1" style={{ color: "#13A69999" }}>
                Photo is always visible. You can choose whether your Degree & Aadhaar are public to explorers. Admin always has full access.
              </p>
              <ul className="mt-3 space-y-3">
                {["photo", "aadhar", "degree"].map((d) => {
                  const path = profile?.documents?.[d];
                  const uploaded = !!path;
                  const togglable = d === "aadhar" || d === "degree";
                  const defaultVis = d === "aadhar" ? "private" : "public";
                  const vis = profile?.doc_visibility?.[d] || defaultVis;
                  const isPublic = vis === "public";
                  return (
                    <li key={d} className="rounded-xl border border-black/5 p-3 space-y-2" data-testid={`doc-row-${d}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-3">
                          {d === "photo" && uploaded && (
                            <img
                              src={`${API}/files/${path}`}
                              alt="Profile"
                              loading="lazy"
                              decoding="async"
                              className="h-12 w-12 rounded-full object-cover border border-black/10"
                              data-testid="doc-photo-thumb"
                            />
                          )}
                          <div>
                            <div className="font-bold capitalize text-sm flex items-center gap-1.5" style={{ color: "#13A699" }}>
                              {d}
                              {togglable && uploaded && (
                                <span
                                  className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                                  style={{ background: isPublic ? "#13A699" : "#FFD708", color: isPublic ? "#FFF7ED" : "#13A699" }}
                                  data-testid={`doc-visibility-badge-${d}`}
                                >
                                  {isPublic ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                                  {isPublic ? "Public" : "Private"}
                                </span>
                              )}
                            </div>
                            <div className="text-xs" style={{ color: uploaded ? "#13A699" : "#DC2626" }}>
                              {uploaded ? <><Check className="inline h-3 w-3" /> Uploaded</> : "Not uploaded"}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {uploaded && (
                            <button
                              type="button"
                              onClick={() => openDoc(d, path)}
                              className="text-xs px-3 py-1.5 rounded-full border border-[#13A699] inline-flex items-center gap-1"
                              style={{ color: "#13A699" }}
                              data-testid={`doc-view-${d}`}
                            >
                              <Eye className="h-3 w-3" /> View
                            </button>
                          )}
                          <label className="btn-yellow text-xs px-3 py-1.5 cursor-pointer" data-testid={`doc-upload-${d}`}>
                            {uploaded ? "Replace" : "Upload"}
                            <input
                              type="file"
                              accept={d === "photo" ? "image/*" : "application/pdf,image/*"}
                              className="hidden"
                              onChange={(e) => e.target.files[0] && uploadDoc(d, e.target.files[0])}
                            />
                          </label>
                          {uploaded && (
                            <button
                              type="button"
                              onClick={() => deleteDoc(d)}
                              className="text-xs px-3 py-1.5 rounded-full border border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                              data-testid={`doc-delete-${d}`}
                            >
                              <Trash2 className="h-3 w-3" /> Delete
                            </button>
                          )}
                        </div>
                      </div>
                      {togglable && uploaded && (
                        <div className="flex items-center justify-between gap-2 pl-1 flex-wrap">
                          <p className="text-[11px]" style={{ color: "#13A69999" }}>
                            {isPublic
                              ? "Explorers can view this document on your profile."
                              : "Only you and Admin can view this document."}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            {d === "degree" && (
                              <button
                                type="button"
                                onClick={() => setShowShareModal(true)}
                                className="text-xs font-bold px-3 py-1.5 rounded-full border border-[#13A699] inline-flex items-center gap-1 hover:bg-[#13A699] hover:text-white"
                                style={{ color: "#13A699" }}
                                data-testid="doc-share-link-btn"
                              >
                                <Share2 className="h-3 w-3" /> Share Link
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => toggleDocVisibility(d)}
                              className="text-xs font-bold px-3 py-1.5 rounded-full border border-[#13A699] inline-flex items-center gap-1 hover:bg-[#13A699] hover:text-white"
                              style={{ color: "#13A699" }}
                              data-testid={`doc-toggle-visibility-${d}`}
                            >
                              {isPublic ? <Lock className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
                              Make {isPublic ? "Private" : "Public"}
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            <form onSubmit={addSlot} className="bg-white rounded-3xl p-6 border border-black/5" data-testid="slot-form">
              <h2 className="font-display text-xl font-bold uppercase" style={{ color: "#13A699" }}>Add Availability</h2>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Input label="Date" type="date" value={slotForm.date} onChange={(v) => setSlotForm({ ...slotForm, date: v })} testId="slot-date" />
                <Input label="Max Bookings" type="number" min="1" value={slotForm.max_bookings} onChange={(v) => setSlotForm({ ...slotForm, max_bookings: v })} testId="slot-max" />
                <Input label="Start" type="time" value={slotForm.start_time} onChange={(v) => setSlotForm({ ...slotForm, start_time: v })} testId="slot-start" />
                <Input label="End" type="time" value={slotForm.end_time} onChange={(v) => setSlotForm({ ...slotForm, end_time: v })} testId="slot-end" />
              </div>
              <button type="submit" className="btn-yellow w-full mt-4 py-2 text-sm" data-testid="slot-add-btn">
                <Plus className="h-4 w-4 inline -mt-0.5 mr-1" /> Add Slot
              </button>

              <h3 className="font-display font-bold mt-5 uppercase tracking-wider" style={{ color: "#13A699" }}>Your Slots</h3>
              {slots.length === 0 ? (
                <p className="text-sm mt-1" style={{ color: "#13A699" }}>No slots yet.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {slots.map((s) => (
                    <li key={s.id} className="rounded-lg border border-black/5 p-2 flex items-center justify-between" data-testid={`my-slot-${s.id}`}>
                      <div className="text-sm" style={{ color: "#13A699" }}>
                        <div className="font-bold">{s.date}</div>
                        <div className="text-xs">{s.start_time}–{s.end_time} · {s.bookings_count}/{s.max_bookings}</div>
                      </div>
                      <button type="button" onClick={() => removeSlot(s.id)} className="text-xs flex items-center gap-1" style={{ color: "#13A699" }} data-testid={`my-slot-delete-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </form>
          </div>
        </div>

        {/* FEATURED LISTING UPSELL */}
        <div
          className="mt-8 rounded-3xl p-6 border-2"
          style={{ background: "#FFD70822", borderColor: "#FFD708" }}
          data-testid="featured-card"
        >
          <div className="flex items-start gap-3 flex-wrap justify-between">
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-bold uppercase flex items-center gap-2" style={{ color: "#13A699" }}>
                <Crown className="h-6 w-6" /> Featured Listing
              </h2>
              <p className="text-sm mt-1 max-w-xl" style={{ color: "#13A699" }}>
                Pin your profile to the top of every search result and stand out from other mentors. Higher visibility = more bookings.
              </p>
            </div>
            {featuredStatus.featured ? (
              <span
                className="inline-flex items-center gap-2 text-xs font-bold uppercase px-3 py-2 rounded-full"
                style={{ background: "#FFD708", color: "#13A699" }}
                data-testid="featured-active-badge"
              >
                <Sparkles className="h-4 w-4" /> Active until {(featuredStatus.featured_until || "").slice(0, 10)}
              </span>
            ) : (
              <span className="text-xs font-bold uppercase px-3 py-2 rounded-full bg-white border border-[#13A699]/30" style={{ color: "#13A699" }} data-testid="featured-inactive-badge">
                Not Featured
              </span>
            )}
          </div>

          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {Object.entries(featuredPlans).map(([key, p]) => (
              <button
                key={key}
                type="button"
                onClick={() => activateFeatured(key)}
                disabled={activatingPlan === key}
                className="text-left bg-white rounded-2xl p-4 border border-black/5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 disabled:opacity-60"
                data-testid={`featured-plan-${key}-btn`}
              >
                <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "#13A699" }}>{p.label}</div>
                <div className="font-display text-2xl font-bold mt-1" style={{ color: "#13A699" }}>₹{p.price}</div>
                <div className="text-xs mt-1" style={{ color: "#13A699" }}>{p.days} days · top of search</div>
                <div className="mt-3 inline-flex items-center text-xs font-bold uppercase px-3 py-1.5 rounded-full" style={{ background: "#FFD708", color: "#13A699" }}>
                  {activatingPlan === key ? "Activating..." : "Activate"}
                </div>
              </button>
            ))}
          </div>
          <p className="text-[11px] mt-3 opacity-75" style={{ color: "#13A699" }}>
            * Payment is currently in test mode — no real charge will be made until a payment provider is connected.
          </p>
        </div>

        {/* Bookings */}
        <div className="mt-8 bg-white rounded-3xl p-6 border border-black/5" data-testid="bookings-card">
          <h2 className="font-display text-xl font-bold uppercase" style={{ color: "#13A699" }}>Demo Class Requests</h2>
          {demoBookings.length === 0 ? (
            <p className="text-sm mt-2" style={{ color: "#13A699" }}>No demo bookings yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {demoBookings.map((d) => (
                <li key={d.id} className="rounded-xl border border-black/5 p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`demo-row-${d.id}`}>
                  <div className="text-sm" style={{ color: "#13A699" }}>
                    <div className="font-bold">{d.other_name}</div>
                    <div className="text-xs">Paid ₹{d.amount} · {(d.paid_at || "").slice(0, 10)}</div>
                    {d.cancel_reason && (
                      <div className="text-xs italic mt-0.5" style={{ color: "#DC2626" }}>Cancel reason: {d.cancel_reason}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {d.status === "cancelled" ? (
                      <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#DC2626", color: "#FFF" }}>
                        Cancelled
                      </span>
                    ) : (
                      <>
                        <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#FFD708", color: "#13A699" }}>Demo Paid</span>
                        <DemoCancelButton demo={d} onChange={load} />
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 bg-white rounded-3xl p-6 border border-black/5" data-testid="bookings-card-regular">
          <h2 className="font-display text-xl font-bold uppercase" style={{ color: "#13A699" }}>Incoming Bookings</h2>
          {bookings.length === 0 ? (
            <p className="text-sm mt-2" style={{ color: "#13A699" }}>No bookings yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {bookings.map((b) => {
                const isPending = b.status === "pending_teacher_approval";
                const isConfirmed = b.status === "confirmed";
                const isClosed = b.status === "completed" || b.status === "cancelled";
                return (
                  <li key={b.id} className="rounded-xl border border-black/5 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`booking-row-${b.id}`}>
                    <div className="text-sm" style={{ color: "#13A699" }}>
                      <div className="font-bold">{b.parent_name} · {b.date}</div>
                      <div className="text-xs">{b.start_time}–{b.end_time}</div>
                      {b.child && (
                        <div className="text-xs mt-0.5" style={{ color: "#13A699" }}>
                          Student: <b>{b.child.name}</b>{b.child.grade ? ` · ${b.child.grade}` : ""}{b.child.school ? ` · ${b.child.school}` : ""}
                          {b.child.location ? <> · <MapPin className="inline h-3 w-3 -mt-0.5" /> {b.child.location}</> : null}
                        </div>
                      )}
                      {b.parent_phone && (b.status === "confirmed" || b.status === "completed") && (
                        <div className="text-xs mt-1 px-2 py-1 rounded-lg inline-block" style={{ background: "#FFF7ED", color: "#13A699" }} data-testid={`explorer-contact-${b.id}`}>
                          📞 {b.parent_phone}
                        </div>
                      )}
                      {b.notes && <div className="text-xs italic mt-0.5" style={{ color: "#13A69999" }}>“{b.notes}”</div>}
                      {b.cancel_reason && (
                        <div className="text-xs italic mt-0.5" style={{ color: "#DC2626" }}>Cancel reason: {b.cancel_reason}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {isPending && (
                        <>
                          <button
                            onClick={() => respondBooking(b.id, "approve")}
                            className="btn-yellow text-xs px-3 py-1.5"
                            data-testid={`approve-booking-${b.id}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              const note = window.prompt("Reason for declining (optional):", "") || "";
                              respondBooking(b.id, "decline", note);
                            }}
                            className="text-xs px-3 py-1.5 rounded-full border border-red-600 text-red-600 hover:bg-red-50"
                            data-testid={`decline-booking-${b.id}`}
                          >
                            Decline
                          </button>
                          <CancelButton booking={b} role="teacher" onChange={load} />
                        </>
                      )}
                      {isConfirmed && (
                        <>
                          <StartSessionButton booking={b} role="teacher" onChange={load} />
                          <PunchInButton booking={b} role="teacher" onChange={load} />
                          <CancelButton booking={b} role="teacher" onChange={load} />
                        </>
                      )}
                      {isClosed && (
                        <>
                          <ReportSessionButton booking={b} role="teacher" onChange={load} />
                          <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{
                            background: b.status === "completed" ? "#13A699" : "#DC2626",
                            color: "#FFF7ED",
                          }}>{b.status_label || b.status}</span>
                        </>
                      )}
                      {b.status === "declined" && (
                        <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#DC2626", color: "#FFF" }}>
                          declined
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Completed Sessions */}
        {(() => {
          const completed = bookings.filter((b) => b.status === "completed");
          return (
            <div className="mt-6 bg-white rounded-3xl p-6 border border-black/5" data-testid="bookings-card-completed">
              <h2 className="font-display text-xl font-bold uppercase inline-flex items-center gap-2" style={{ color: "#13A699" }}>
                <Check className="h-5 w-5" /> Completed Sessions ({completed.length})
              </h2>
              <p className="text-xs mt-1" style={{ color: "#13A69999" }}>
                Sessions where both you and the explorer punched in. Payout is queued for admin release.
              </p>
              {completed.length === 0 ? (
                <p className="mt-3 text-sm" style={{ color: "#13A69999" }} data-testid="completed-empty-teacher">
                  No completed sessions yet.
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {completed.map((b) => (
                    <li
                      key={b.id}
                      className="rounded-xl border border-black/5 p-3 flex items-center justify-between flex-wrap gap-2"
                      data-testid={`completed-session-teacher-${b.id}`}
                    >
                      <div>
                        <div className="font-bold text-sm" style={{ color: "#13A699" }}>{b.parent_name}</div>
                        <div className="text-xs" style={{ color: "#13A69999" }}>{b.date} · {b.start_time}–{b.end_time}</div>
                        {b.child && (
                          <div className="text-xs mt-0.5" style={{ color: "#13A69988" }}>
                            Student: <b>{b.child.name}</b>{b.child.grade ? ` · ${b.child.grade}` : ""}{b.child.location ? <> · <MapPin className="inline h-3 w-3 -mt-0.5" /> {b.child.location}</> : null}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {b.payout_amount > 0 && (
                          <span className="text-xs font-bold" style={{ color: "#13A699" }}>
                            Payout: ₹{b.payout_amount}
                          </span>
                        )}
                        <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#13A699", color: "#FFF7ED" }}>Completed</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })()}

        <TeacherCoursesPanel />
      </section>

      {/* Share Link Modal */}
      {showShareModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.45)" }}
          data-testid="share-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowShareModal(false); }}
        >
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full border border-black/5 max-h-[90vh] overflow-y-auto" data-testid="share-modal">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-xl font-bold uppercase flex items-center gap-2" style={{ color: "#13A699" }}>
                  <Share2 className="h-5 w-5" /> Share Degree Link
                </h3>
                <p className="text-xs mt-1" style={{ color: "#13A69999" }}>
                  Generate a temporary link to share your Verified Degree with a recruiter or institution. The link auto-expires and can be revoked anytime.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                className="text-[#13A699] hover:opacity-70"
                data-testid="share-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5">
              <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Link expires in</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[1, 7, 30].map((d) => {
                  const active = shareExpiryDays === d;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setShareExpiryDays(d)}
                      className="text-sm font-bold px-3 py-2 rounded-2xl border"
                      style={{
                        background: active ? "#FFD708" : "#FFF7ED",
                        color: "#13A699",
                        borderColor: active ? "#FFD708" : "#13A69922",
                      }}
                      data-testid={`share-expiry-${d}`}
                    >
                      {d === 1 ? "1 day" : d === 7 ? "7 days" : "30 days"}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={createShareLink}
                disabled={creatingShare}
                className="btn-yellow w-full mt-4 py-2.5 text-sm disabled:opacity-60"
                data-testid="share-generate-btn"
              >
                {creatingShare ? "Generating..." : "Generate Link"}
              </button>
            </div>

            <div className="mt-6">
              <h4 className="font-display font-bold text-sm uppercase" style={{ color: "#13A699" }}>
                Active Links ({shareLinks.length})
              </h4>
              {shareLinks.length === 0 ? (
                <p className="text-xs mt-2" style={{ color: "#13A69999" }}>No active share links yet.</p>
              ) : (
                <ul className="mt-2 space-y-2" data-testid="share-links-list">
                  {shareLinks.map((ln) => (
                    <li key={ln.token} className="rounded-xl border border-black/5 p-3" data-testid={`share-link-${ln.token}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-xs flex items-center gap-1.5" style={{ color: "#13A699" }}>
                          <Clock className="h-3 w-3" /> {fmtExpiry(ln.expires_at)}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => copyShareLink(ln.share_url)}
                            className="text-xs px-3 py-1.5 rounded-full border border-[#13A699] inline-flex items-center gap-1"
                            style={{ color: "#13A699" }}
                            data-testid={`share-copy-${ln.token}`}
                          >
                            <Copy className="h-3 w-3" /> Copy
                          </button>
                          <button
                            type="button"
                            onClick={() => revokeShareLink(ln.token)}
                            className="text-xs px-3 py-1.5 rounded-full border border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                            data-testid={`share-revoke-${ln.token}`}
                          >
                            <Trash2 className="h-3 w-3" /> Revoke
                          </button>
                        </div>
                      </div>
                      <div
                        className="mt-2 text-[11px] break-all rounded-lg px-2 py-1.5 bg-[#FFF7ED]"
                        style={{ color: "#13A699" }}
                      >
                        {ln.share_url}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId, ...rest }) {
  return (
    <label className="block text-sm font-semibold" style={{ color: "#13A699" }}>
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
        data-testid={testId}
        {...rest}
      />
    </label>
  );
}

function Textarea({ label, value, onChange, testId }) {
  return (
    <label className="block mt-3 text-sm font-semibold" style={{ color: "#13A699" }}>
      {label}
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
        data-testid={testId}
      />
    </label>
  );
}
